import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { toast } from "sonner"
import { PageHeader, Section, SectionStack } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { SettingRow, SettingRows } from "@/features/integration-apps/SettingRow"
import {
	useBackfillAttioProfiles,
	useBackfillBrazeProfiles,
} from "@/queries/marketplace"

export const Route = createFileRoute("/tools/crm/")({
	component: CrmPage,
})

type Population = "sellers_with_live_listings" | "active_users"

function CrmPage() {
	const [population, setPopulation] = useState<Population>(
		"sellers_with_live_listings",
	)
	const [sinceDays, setSinceDays] = useState("90")
	const mutation = useBackfillBrazeProfiles()
	const attioMutation = useBackfillAttioProfiles()

	const handleRun = () => {
		mutation.mutate(
			{
				population,
				sinceDays:
					population === "active_users" ? Number(sinceDays) || 90 : undefined,
			},
			{
				onSuccess: () => {
					toast.success("Braze profile backfill queued", {
						description:
							"Refresh requests are being enqueued in batches; only changed attributes reach Braze.",
					})
				},
				onError: (error) => {
					toast.error("Braze profile backfill failed", {
						description: error.message,
					})
				},
			},
		)
	}

	const handleRunAttio = () => {
		attioMutation.mutate(
			{ population: "pro_sellers" },
			{
				onSuccess: () => {
					toast.success("Attio profile backfill queued", {
						description:
							"Refresh requests are being enqueued in batches; only changed attributes reach Attio.",
					})
				},
				onError: (error) => {
					toast.error("Attio profile backfill failed", {
						description: error.message,
					})
				},
			},
		)
	}

	return (
		<div className="flex max-w-4xl flex-col gap-6 p-6">
			<PageHeader
				title="CRM"
				description="Backend-owned profile attributes are pushed to Braze and Attio on activity and domain events. Run a backfill after adding an attribute, at launch, or to recover from an outage."
			/>

			<SectionStack>
				<Section label="Braze: backfill profile attributes">
					<SettingRows>
						<SettingRow
							label="Population"
							htmlFor="braze-backfill-population"
							help="Queues one refresh per user in the population. Each refresh recomputes the profile and pushes only the keys that changed."
						>
							<Select
								value={population}
								onValueChange={(value) => setPopulation(value as Population)}
							>
								<SelectTrigger
									id="braze-backfill-population"
									className="w-full"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="sellers_with_live_listings">
										Sellers with live listings
									</SelectItem>
									<SelectItem value="active_users">
										Users active in the last N days
									</SelectItem>
								</SelectContent>
							</Select>
						</SettingRow>
						{population === "active_users" && (
							<SettingRow label="Days" htmlFor="braze-backfill-since">
								<Input
									id="braze-backfill-since"
									type="number"
									min={1}
									value={sinceDays}
									onChange={(e) => setSinceDays(e.target.value)}
									className="w-24 tabular-nums"
								/>
							</SettingRow>
						)}
					</SettingRows>
					<div>
						<Button onClick={handleRun} disabled={mutation.isPending}>
							{mutation.isPending ? "Queuing…" : "Run backfill"}
						</Button>
					</div>
				</Section>

				<Section label="Attio: backfill pro sellers">
					<p className="max-w-2xl text-muted-foreground">
						Pro sellers (declared at account onboarding or who started pro
						seller onboarding) are pushed to Attio as a company with a linked
						person. Each refresh recomputes the profile and pushes only the
						attributes that changed.
					</p>
					<div>
						<Button
							variant="outline"
							onClick={handleRunAttio}
							disabled={attioMutation.isPending}
						>
							{attioMutation.isPending ? "Queuing…" : "Run backfill"}
						</Button>
					</div>
				</Section>
			</SectionStack>
		</div>
	)
}
